import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Lock as LockIcon, Login as LoginIcon } from '@mui/icons-material';
import Logo from '../common/Logo';
import TechFigures from '../common/TechFigures';
import { useAccessGate } from '../../contexts/AccessGateContext';

const AccessGateScreen: React.FC = () => {
  const { unlock } = useAccessGate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!password.trim()) {
      setError('Please enter the access code');
      return;
    }

    setLoading(true);
    try {
      const success = await unlock(password);
      if (!success) {
        setError('Invalid access code. Please try again.');
        setPassword('');
      }
    } catch {
      setError('Unable to verify access code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0927eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: '#0927eb',
          zIndex: 0,
        }}
      >
        <TechFigures />
      </div>

      <Paper
        elevation={24}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0927eb 0%, #0619A8 100%)',
            color: 'white',
            py: 4,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Logo size="large" />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Private Access
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Enter the access code to continue
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Access Code"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #0927eb 0%, #0619A8 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0619A8 0%, #041280 100%)',
                },
              }}
            >
              {loading ? 'Verifying...' : 'Enter'}
            </Button>
          </form>
        </Box>
      </Paper>
    </div>
  );
};

export default AccessGateScreen;
